import i18n from '@/utils/i18n';
import { mapAlertLevelToToastVariant } from '@sales-portal/domain/pricing/screens/management/blocs/utils/toast-utils';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingVariantPackCostChangeDataDocument } from '@sales-portal/domain/pricing/screens/management/blocs/documents/variant-pack-cost-change-data.generated';
import {
  PricingVariantPackCostChangeDataQuery,
  VariantPackCostChangeDataFieldsFragment,
} from '@/types';
import { useCallback, useMemo, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSubmitPricingPriceDraftUpdatesForBusinessOrganizationMutation } from '@sales-portal/domain/pricing/screens/management/blocs/documents/submit-price-draft.generated';
import { useSyncAndVerifyPriceDraftEdits } from '@sales-portal/domain/pricing/blocs/mutators/use-update-price-draft-mutator';

const costChangeIdentity = (
  item: VariantPackCostChangeDataFieldsFragment,
): string =>
  `${item.restaurant?.uuid ?? ''}:${item.variant_pack?.uuid ?? ''}:${item.supplier_id}`;

export const useSubmitPriceDraftMutator = () => {
  const [loading, setLoading] = useState(false);
  const { showErrorToast, showToast } = useExperienceToasts();
  const { syncAndVerify } = useSyncAndVerifyPriceDraftEdits();
  const [submitPricingUpdatesMutation] =
    useSubmitPricingPriceDraftUpdatesForBusinessOrganizationMutation();

  const submitPriceDrafts = useCallback(
    async (onSubmitSuccess: () => void) => {
      try {
        setLoading(true);
        const syncAndVerifyResult = await syncAndVerify();
        const syncResponse =
          syncAndVerifyResult?.data
            ?.upsertPricingPriceDraftUpdatesForBusinessOrganization;

        if (!syncResponse) {
          return;
        }

        const {
          cost_changes: costChanges,
          per_supplier: upsertPerSupplier,
          total_upsert_count: totalUpsertCount,
        } = syncResponse;

        if (!costChanges || totalUpsertCount === 0) {
          return;
        }

        pricingManagementAnalytics.track(PricingAnalyticEvent.PUBLISH, {
          editRestaurantVPPairs: costChanges.map(
            (change) =>
              `${change.restaurant?.uuid}:${change.variant_pack?.uuid}`,
          ),
          numberOfEdits: totalUpsertCount,
          supplierUUIDs: upsertPerSupplier.map((entry) => entry.supplier_id),
        });

        const upsertCountBySupplier = new Map(
          upsertPerSupplier.map((entry) => [
            entry.supplier_id,
            entry.upsert_count,
          ]),
        );

        return await submitPricingUpdatesMutation({
          onCompleted: (submitPricingUpdates) => {
            const perSupplier =
              submitPricingUpdates
                .submitPricingPriceDraftUpdateBatchesForBusinessOrganization
                .per_supplier;

            // One toast per supplier: surface its alert, or a success
            // message scoped to that supplier's upsert count.
            perSupplier.forEach((result) => {
              if (result.alert) {
                showToast({
                  title: result.alert.alert_message,
                  variant: mapAlertLevelToToastVariant(
                    result.alert.alert_level,
                  ),
                });
                return;
              }

              const upsertCount =
                upsertCountBySupplier.get(result.supplier_id) ?? 0;
              if (upsertCount) {
                showToast({
                  title: i18n.t(
                    `pricing.publish.success_message.${upsertCount === 1 ? 'singular' : 'plural'}`,
                    {
                      count: upsertCount,
                    },
                  ),
                  variant: 'success',
                });
              }
            });
            onSubmitSuccess();
          },
          // Inject the returned cost-change data into the cache for the
          // getVariantPackCostChangeDataForBusinessOrganization query instead
          // of refetching it. The mutation returns rows only for the submitted
          // edits, so merge them into the existing (org-wide) cached list by
          // restaurant + variant pack + supplier identity.
          update: (cache, { data }) => {
            const costChanges =
              data?.submitPricingPriceDraftUpdateBatchesForBusinessOrganization
                ?.cost_changes;
            if (!costChanges?.length) {
              return;
            }

            const existing =
              cache.readQuery<PricingVariantPackCostChangeDataQuery>({
                query: PricingVariantPackCostChangeDataDocument,
              });
            if (!existing) {
              return;
            }

            const mergedByIdentity = new Map(
              existing.getVariantPackCostChangeDataForBusinessOrganization.map(
                (item) => [costChangeIdentity(item), item],
              ),
            );
            costChanges.forEach((item) => {
              mergedByIdentity.set(costChangeIdentity(item), item);
            });

            cache.writeQuery<PricingVariantPackCostChangeDataQuery>({
              data: {
                getVariantPackCostChangeDataForBusinessOrganization: Array.from(
                  mergedByIdentity.values(),
                ),
              },
              query: PricingVariantPackCostChangeDataDocument,
            });
          },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : i18n.t('pricing.verify_edits.unknown_error');
        showErrorToast(errorMessage);
        pricingManagementAnalytics.track(PricingAnalyticEvent.PUBLISH_ERROR, {
          error: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [showErrorToast, showToast, submitPricingUpdatesMutation, syncAndVerify],
  );

  return useMemo(
    () => ({
      loading,
      submitPriceDrafts,
    }),
    [loading, submitPriceDrafts],
  );
};
